import { Component, EventEmitter, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { ModalV2Component } from "../../../../../shared/modal-v2/modal-v2.component";
import { DiaryFormElement } from "../../../../../core/models/trial-diary-form/diary-form-element.model";
import { BaseComponent } from "../../../../../shared/base-component";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { DiaryFormElementType } from '../../../../../core/constants/diary-form-element-type.enum';
import { Cultures } from "../../../../../core/constants/cultures";
import { InputSelectComponent } from "../../../../../shared/input-select/input-select.component";
import {
  DiaryFormElementTranslation
} from "../../../../../core/models/trial-diary-form/diary-form-element-translation.model";
import {
  noWhitespaceValidator,
  requiredNoWhitespaceValidator
} from "../../../../../core/validators/no-whitespace.validator";

@Component({
  selector: 'app-element-settings-modal',
  templateUrl: './element-settings-modal.component.html',
  styleUrl: './element-settings-modal.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ElementSettingsModalComponent extends BaseComponent {
  @ViewChild('modal') modal: ModalV2Component;
  @ViewChild('cultureSelect') cultureSelect: InputSelectComponent;

  @Output() updated = new EventEmitter<DiaryFormElement>();

  protected element: DiaryFormElement;
  private originalElement: DiaryFormElement;
  protected editable: boolean = false;
  protected isOnboardingQuestions: boolean = false;
  protected get completionOptions(): { value: string, text: string }[] {
    return this.element?.options?.map(o => ({ value: o.id, text: o.text })) ?? [];
  }
  availableCultures: { value: string, text: string }[] = [];
  filteredCultures: { value: string, text: string }[] = [];

  protected form = new FormGroup({
    isBusy: new FormControl<boolean>(false),
    selectedCulture: new FormControl<string>('en'),
    label: new FormControl<string|null>(null, requiredNoWhitespaceValidator()),
    placeholder: new FormControl<string|null>(null, noWhitespaceValidator()),
    completionValue: new FormControl<string|null>(null),
    completionOptionId: new FormControl<string|null>(null),
    isRequired: new FormControl<boolean>(false),
  });
  protected cultureForms: FormGroup[] = [];

  constructor() {
    super();

    Cultures.all().forEach(culture => {
      this.availableCultures.push({ value: culture.culture, text: culture.name });
    });
    this.filteredCultures = this.availableCultures;
  }

  elementIsEditable() {
    if (!this.element)
      return false;

    return this.editable;
  }

  labelIsEditable() {
    if (!this.element)
      return false;

    if (this.form.get('selectedCulture').value !== 'en') {
      let translation = this.element.translations.find(t => t.culture === this.form.get('selectedCulture').value);
      if (!translation || (translation && (translation.label === null || translation.label === '')))
        return true;
    }

    return this.editable;
  }

  placeholderIsEditable() {
    if (!this.element)
      return false;

    if (this.form.get('selectedCulture').value !== 'en') {
      let translation = this.element.translations.find(t => t.culture === this.form.get('selectedCulture').value);
      if (!translation || (translation && (translation.placeholder === null || translation.placeholder === '')))
        return true;
    }

    return this.editable;
  }

  onSelectLanguage(language: string) {
    const culture = this.availableCultures.find(t => t.text === language);
    if (culture) {
      this.form.patchValue({selectedCulture: culture.value});
    }
  }

  onCultureFiltered(value: string) {
    // Trim the input value and convert it to lowercase for case-insensitive filtering
    const filterValue = value.trim().toLowerCase();

    this.filteredCultures = this.availableCultures.filter(x =>
        x.text.toLowerCase().includes(filterValue)
    );
  }

  shouldShowPlaceholder() {
    if (this.element?.elementType === DiaryFormElementType.Checkbox || this.element?.elementType === DiaryFormElementType.Radio || this.element?.elementType === DiaryFormElementType.Date || this.element?.elementType === DiaryFormElementType.Time)
      return false;

    return true;
  }

  openManageElementModal(element: DiaryFormElement, editable: boolean = true, isOnboardingQuestions: boolean = false) {
    // Store reference to the original element
    this.originalElement = element;
    // Create a deep clone of the element to prevent changes from propagating to parent until Update is pressed
    this.element = new DiaryFormElement(element);
    this.editable = editable;
    this.isOnboardingQuestions = isOnboardingQuestions;


    this.form.reset();
    this.cultureSelect.onClear();

    this.form.patchValue({
      selectedCulture: 'en',
      label: element.label,
      placeholder: element.placeholder,
      completionValue: element.completionValue,
      completionOptionId: element.completionOptionId,
      isRequired: element.isRequired,
    });

    // Set completion answer validators
    const completionValueCtrl = this.form.get('completionValue');
    const completionOptionIdCtrl = this.form.get('completionOptionId');
    completionValueCtrl.clearValidators();
    completionOptionIdCtrl.clearValidators();
    if (isOnboardingQuestions && this.isCompletionAnswerSupported()) {
      if (this.isOptionBasedCompletion()) {
        completionOptionIdCtrl.setValidators([Validators.required]);
      } else {
        completionValueCtrl.setValidators([Validators.required]);
      }
    }
    completionValueCtrl.updateValueAndValidity();
    completionOptionIdCtrl.updateValueAndValidity();

    // Build culture forms
    this.cultureForms = [];
    this.availableCultures.forEach(culture => {
      const translation = this.element.translations.find(t => t.culture === culture.value);
      if (culture.value !== 'en') {
        this.cultureForms.push(new FormGroup({
          id: new FormControl<string>(translation?.id),
          culture: new FormControl<string>(culture.value),
          label: new FormControl<string>(translation?.label, noWhitespaceValidator()),
          placeholder: new FormControl<string>(translation?.placeholder, noWhitespaceValidator()),
        }));
      }
    });

    this.modal.show();
  }

  closeManageElementModal() {
    this.element = null;
    this.originalElement = null;
    this.modal.hide();
  }

  protected isCompletionAnswerSupported(): boolean {
    return [
      DiaryFormElementType.Radio,
      DiaryFormElementType.Dropdown,
      DiaryFormElementType.Numeric
    ].includes(this.element?.elementType);
  }

  protected get selectedCompletionOptionText(): string {
    const id = this.form.get('completionOptionId').value;
    return this.completionOptions.find(o => o.value === id)?.text ?? '';
  }

  protected isOptionBasedCompletion(): boolean {
    return [
      DiaryFormElementType.Radio,
      DiaryFormElementType.Dropdown
    ].includes(this.element?.elementType);
  }

  get showOptionsSection(): boolean {
    return [
      DiaryFormElementType.Checkbox,
      DiaryFormElementType.Radio,
      DiaryFormElementType.Dropdown
    ].includes(this.element?.elementType);
  }

  onUpdateElementSettings() {
    if (this.form.invalid)
      return;

    let form = this.form;

    this.element.label = form.value.label;
    this.element.isRequired = form.value.isRequired;
    this.element.placeholder = form.value.placeholder;

    // Apply translations to the element
    this.cultureForms.forEach(cultureForm => {
      const translation = this.element.translations.find(t => t.culture === cultureForm.value.culture);
      if (translation) {
        translation.label = cultureForm.value.label;
        translation.placeholder = cultureForm.value.placeholder;
      } else {
        this.element.translations.push(new DiaryFormElementTranslation({
          culture: cultureForm.value.culture,
          label: cultureForm.value.label,
          placeholder: cultureForm.value.placeholder
        }));
      }
    });

    // Copy all changes from the cloned element back to the original element
    this.originalElement.label = this.element.label;
    this.originalElement.isRequired = this.element.isRequired;
    this.originalElement.placeholder = this.element.placeholder;
    this.originalElement.options = this.element.options;
    this.originalElement.translations = this.element.translations;

    if (this.isOnboardingQuestions && this.isCompletionAnswerSupported()) {
      if (this.isOptionBasedCompletion()) {
        this.originalElement.completionOptionId = this.form.value.completionOptionId || null;
        this.originalElement.completionValue = null;
      } else {
        this.originalElement.completionValue = this.form.value.completionValue || null;
        this.originalElement.completionOptionId = null;
      }
    } else {
      this.originalElement.completionValue = null;
      this.originalElement.completionOptionId = null;
    }

    this.updated.emit(this.originalElement);

    this.modal.hide();
  }

  elementOptionsValid() {
    if (!this.element) {
      return true;
    }

    if (this.element.elementType === DiaryFormElementType.Dropdown || this.element.elementType === DiaryFormElementType.Radio || this.element.elementType === DiaryFormElementType.Checkbox) {
      // Check if options exist
      if (this.element.options.length === 0) {
        return false;
      }

      // Check if all options have non-empty English text and translations aren't whitespace-only
      return this.element.options.every(option =>
        option.text && option.text.trim().length > 0 &&
        option.translations.every(t => !t.text || t.text.trim().length > 0)
      );
    }

    return true;
  }

  protected readonly DiaryFormElementType = DiaryFormElementType;
}
